import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C, card, sectionTitle, filterPill, kpiCard, kpiIcon, toggleGroup, toggleBtn, th, td, tdR } from '../../styles';
import { getStaffPerformance, getStaffAttendance, getStaffTrend,
  getSalesFilterOptions, getVisualPrefs, setVisualPref } from '../../services/api';
const ReactECharts = require('echarts-for-react').default;

// ─── helpers ──────────────────────────────────────────────────────────────────

const parseLocalDate = (str) => {
  if (!str) return null;
  const s = String(str);
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  const d = new Date(s);
  if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return null;
};

const fmtDate = (str, opts) => {
  const d = parseLocalDate(str);
  return d ? d.toLocaleDateString('en-GB', opts) : str;
};

const toISOKey = (str) => {
  if (!str) return '';
  const m = String(str).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }
  return String(str);
};

const isMultiYear = (isoKeys) => {
  if (!isoKeys.length) return false;
  return new Set(isoKeys.map(p => String(p).slice(0, 4))).size > 1;
};

const buildMarkArea = (isoKeys) => {
  if (!isoKeys.length) return [];
  const groups = {};
  isoKeys.forEach(p => {
    const yr = String(p).slice(0, 4);
    if (!groups[yr]) groups[yr] = { start: p, end: p };
    groups[yr].end = p;
  });
  return Object.entries(groups).map(([, { start, end }], i) => [
    { xAxis: start, itemStyle: { color: i % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)' }, label: { show: false } },
    { xAxis: end }
  ]);
};

const makeLabelFmt = (period, multiYear) => (v) => {
  try {
    const d = parseLocalDate(v);
    if (!d) return v;
    if (period === 'monthly') {
      const mmm = d.toLocaleDateString('en-GB', { month:'short' });
      if (multiYear && d.getMonth() === 0) return mmm + '\n' + d.getFullYear();
      return mmm;
    }
    const label = d.toLocaleDateString('en-GB', { day:'numeric', month:'short' });
    if (multiYear && d.getDate() <= 7 && d.getMonth() === 0) return label + '\n' + d.getFullYear();
    return label;
  } catch { return v; }
};

const sortPeriodKeys = (periodArray) =>
  periodArray.map(p => toISOKey(p)).sort((a, b) => {
    const da = parseLocalDate(a), db = parseLocalDate(b);
    return da && db ? da - db : 0;
  });

const generateFilledPeriodKeys = (sortedISO, granularity) => {
  if (!sortedISO.length) return sortedISO;
  const start = parseLocalDate(sortedISO[0]);
  const end   = parseLocalDate(sortedISO[sortedISO.length - 1]);
  if (!start || !end) return sortedISO;
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const result = [];
  const cur = new Date(start);
  while (cur <= end) {
    result.push(fmt(cur));
    if (granularity === 'monthly') cur.setMonth(cur.getMonth() + 1);
    else if (granularity === 'weekly') cur.setDate(cur.getDate() + 7);
    else cur.setDate(cur.getDate() + 1);
  }
  return result;
};

// Sort paired (label, value) arrays by value descending, keeping colours consistent
const sortDesc = (labels, values) => {
  const paired = labels.map((l, i) => ({ l, v: values[i] }));
  paired.sort((a, b) => b.v - a.v);
  return { labels: paired.map(p => p.l), values: paired.map(p => p.v) };
};

// ─── constants ────────────────────────────────────────────────────────────────

const STAFF_COLORS = [C.accent, C.blue, C.purple, C.amber, '#FF6B6B', '#34D399', '#F472B6', '#38BDF8'];

// ─── custom legend helpers ────────────────────────────────────────────────────

const useChartLegend = (items) => {
  const [selected, setSelected] = React.useState(() => new Set(items));
  const itemsKey = items.join('||');
  React.useEffect(() => { setSelected(new Set(items)); }, [itemsKey]); // eslint-disable-line
  const toggle = (chartRef, name) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendToggleSelect', name });
    setSelected(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  };
  const selectAll = (chartRef) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendAllSelect' });
    setSelected(new Set(items));
  };
  const resetAll = (chartRef) => {
    const c = chartRef?.current?.getEchartsInstance?.();
    if (c) { c.dispatchAction({ type: 'legendAllSelect' }); c.dispatchAction({ type: 'legendInverseSelect' }); }
    setSelected(new Set());
  };
  return { selected, toggle, selectAll, resetAll };
};

const ChartLegend = ({ items, colors, chartRef, selected, onToggle, onSelectAll, onReset }) => (
  <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px 10px', padding:'6px 0 0' }}>
    <button onClick={() => onSelectAll(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.accent}`, borderRadius:'4px', color:C.accent, cursor:'pointer', flexShrink:0 }}>
      All
    </button>
    <button onClick={() => onReset(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer', flexShrink:0 }}>
      Reset
    </button>
    {items.map((name, i) => {
      const isSel = selected.has(name);
      return (
        <div key={name} onClick={() => onToggle(chartRef, name)}
          style={{ display:'flex', alignItems:'center', gap:'3px', cursor:'pointer', opacity: isSel ? 1 : 0.3, flexShrink:0 }}>
          <div style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors[i % colors.length] }}/>
          <span style={{ fontSize:'9px', color: C.textSec }}>{name}</span>
        </div>
      );
    })}
  </div>
);

const QUICK = [
  { id: 'yesterday',       label: 'Yesterday'        },
  { id: 'this_year',       label: 'This Year'         },
  { id: 'this_month',      label: 'This Month'        },
  { id: 'this_week',       label: 'This Week'         },
  { id: 'last_year',       label: 'Last Year'         },
  { id: 'last_month',      label: 'Last Month'        },
  { id: 'last_week',       label: 'Last Week'         },
  { id: 'last_year_month', label: "Last Year's Month" },
  { id: 'custom',          label: 'Custom'            },
  { id: 'all',             label: 'All Time'          },
];

// ─── sub-components ───────────────────────────────────────────────────────────

const KPICard = ({ title, value, icon, iconBg, sub }) => (
  <div style={kpiCard}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'5px' }}>
      <div style={kpiIcon(iconBg)}>{icon}</div>
    </div>
    <p style={{ fontSize:'11px', color:C.textSec, margin:'0 0 4px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'0.5px' }}>{title}</p>
    <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0, lineHeight:1.2 }}>{value}</p>
    {sub && <p style={{ fontSize:'10px', color:C.textMuted, margin:'3px 0 0' }}>{sub}</p>}
  </div>
);

const CollapsibleSection = ({ visKey, title, collapsed, onToggle, headerRight, children }) => (
  <div style={{ ...card, marginBottom:'12px' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: collapsed ? 0 : '8px' }}>
      <p style={{ ...sectionTitle, marginBottom:0 }}>{title}</p>
      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
        {!collapsed && headerRight}
        <button
          onClick={() => onToggle(visKey)}
          style={{ fontSize:'10px', color:C.textSec, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'3px 8px', cursor:'pointer' }}
        >
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>
    </div>
    {!collapsed && children}
  </div>
);

// ─── FilterPill - immediate apply (matches Bookings tab visual) with search ────
const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen]     = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val];
    onChange(next);
  };

  const filtered = (options || []).filter(opt => {
    const lbl = typeof opt === 'object' ? (opt.label || opt.name || '') : (opt || '');
    return String(lbl).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div ref={ref} style={{ position:'relative', flex:1, minWidth:'130px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', padding:'4px 10px', background:C.bgSurface, border:`1px solid ${selected.length ? C.accent : C.border}`, borderRadius:'8px', fontSize:'11px', color: selected.length ? C.accent : C.textMuted, textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
      >
        <span>{label}{selected.length ? ` (${selected.length})` : ''}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:300, background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>
          <div style={{ padding:'6px 8px', borderBottom:`1px solid ${C.border}` }}>
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." onClick={e => e.stopPropagation()}
              style={{ width:'100%', padding:'5px 8px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'6px', fontSize:'11px', color:C.textPrimary, outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ maxHeight:'150px', overflowY:'auto' }}>
            {filtered.length === 0 && <p style={{ padding:'8px 10px', fontSize:'11px', color:C.textMuted }}>No results</p>}
            {filtered.map((opt, i) => {
              const val = typeof opt === 'object' ? (opt.key || opt.code || String(opt)) : String(opt || '');
              const lbl = typeof opt === 'object' ? (opt.label || opt.name || val) : String(opt || '');
              const isSel = selected.includes(val);
              return (
                <div key={i} onClick={() => toggle(val)} style={{ padding:'7px 10px', fontSize:'11px', color: isSel ? C.accent : C.textSec, background: isSel ? C.accentDim : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
                  <span style={{ width:'12px', height:'12px', border:`1px solid ${isSel ? C.accent : C.border}`, borderRadius:'3px', background: isSel ? C.accent : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}
                  </span>
                  {lbl}
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ padding:'6px 8px', borderTop:`1px solid ${C.border}` }}>
              <button onClick={() => { onChange([]); setOpen(false); }}
                style={{ fontSize:'10px', color:C.danger, background:'none', border:'none', cursor:'pointer' }}>Clear All</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ─── bar chart builder (with descending sort built in) ────────────────────────

const buildBarOption = (rawLabels, rawValues, colors, yFormatter) => {
  const { labels, values } = sortDesc(rawLabels, rawValues);
  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', axisPointer:{ type:'shadow' },
      backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: p => `${p[0].name}<br/><b>${Number(p[0].value).toLocaleString()}</b>`,
    },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: {
      type:'category', data: labels,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: {
        color:C.textMuted, fontSize:9, rotate:30, interval: 0, hideOverlap: true,
        formatter: v => v && v.length > 10 ? v.slice(0, 10) + '…' : v,
      },
    },
    yAxis: {
      type:'value', splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: yFormatter || (v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v) },
    },
    series: [{
      type:'bar',
      data: values.map((v, i) => ({ value: v, itemStyle: { color: colors[i % colors.length], borderRadius:[4,4,0,0] } })),
    }],
  };
};

// ─── sortable table helpers ───────────────────────────────────────────────────

const SortTh = ({ col, label, sortCol, sortDir, onSort, style }) => (
  <th onClick={() => onSort(col)}
    style={{ ...style, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
    {label}
    <span style={{ marginLeft:'4px', opacity: sortCol === col ? 1 : 0.3, fontSize:'9px' }}>
      {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
    </span>
  </th>
);

const useSortedData = (data, defaultCol, defaultDir = 'desc') => {
  const [sortCol, setSortCol] = React.useState(defaultCol);
  const [sortDir, setSortDir] = React.useState(defaultDir);
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const sorted = [...data].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    const an = parseFloat(av), bn = parseFloat(bv);
    const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av || '').localeCompare(String(bv || ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return { sorted, sortCol, sortDir, toggleSort };
};

// ─── main component ───────────────────────────────────────────────────────────

const StaffPerformance = () => {
  const [activePeriod, setAP]           = useState(() => localStorage.getItem('ccs_period') || 'this_month');
  const setPeriod = (p) => { localStorage.setItem('ccs_period', p); setAP(p); };
  // ── Branch filter (boss + area_manager only, persists in localStorage) ────
  const [branches, setBranches] = useState([]);
  const _initBranches = () => { try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; } };
  const [selectedBranches, setSelected] = useState(_initBranches);
  const [pendingBranches,  setPending]  = useState(_initBranches);
  const [showBranchDrop, setShowBranchDrop] = useState(false);
  const openBranchDrop = () => {if (showBranchDrop) { setShowBranchDrop(false); } else { setPending(selectedBranches); setShowBranchDrop(true); }};
  const toggleBranch    = (code) => setPending(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  const applyBranches   = () => { localStorage.setItem('ccs_branch', JSON.stringify(pendingBranches)); setSelected(pendingBranches); setShowBranchDrop(false); };
  const resetBranches   = () => { setPending([]); setSelected([]); localStorage.setItem('ccs_branch', '[]'); setShowBranchDrop(false); };

  useEffect(() => {
    import('../../services/api').then(({ default: api }) => {
      api.get('/api/branches').then(r => setBranches(r.data.branches || [])).catch(() => {});
    });
  }, []);

    // Compute date range synchronously from period string
  const _computeDates = (period) => {
    const now = new Date();
    const _p  = n => String(n).padStart(2, '0');
    const _f  = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    if (period === 'yesterday')      { const y = new Date(now); y.setDate(y.getDate()-1); return [_f(y), _f(y)]; }
    if (period === 'this_month')     return [_f(new Date(now.getFullYear(), now.getMonth(), 1)), _f(now)];
    if (period === 'this_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day+1); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'this_year')      return [_f(new Date(now.getFullYear(), 0, 1)), _f(now)];
    if (period === 'last_year')      return [_f(new Date(now.getFullYear()-1, 0, 1)), _f(new Date(now.getFullYear()-1, 11, 31))];
    if (period === 'last_month')     return [_f(new Date(now.getFullYear(), now.getMonth()-1, 1)), _f(new Date(now.getFullYear(), now.getMonth(), 0))];
    if (period === 'last_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day-6); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'last_year_month'){ const m = now.getMonth(), ly = now.getFullYear()-1; return [_f(new Date(ly,m,1)), _f(new Date(ly,m+1,0))]; }
    if (period === 'custom')        return [localStorage.getItem('ccs_from')||'', localStorage.getItem('ccs_to')||''];
    return ['', ''];
  };
  const _initPeriod = localStorage.getItem('ccs_period') || 'this_month';
  const [_initFrom, _initTo] = _computeDates(_initPeriod);
  const _initPendFrom = _initPeriod === 'custom' ? (localStorage.getItem('ccs_from')||'') : '';
  const _initPendTo   = _initPeriod === 'custom' ? (localStorage.getItem('ccs_to')||'')   : '';
const [dateFrom, setDateFrom]         = useState(_initFrom);
  const [dateTo, setDateTo]             = useState(_initTo);
  const [pendingFrom, setPF]            = useState(_initPendFrom);
  const [pendingTo, setPT]              = useState('');

  const [performance, setPerformance]   = useState([]);
  const [perfTotals, setPerfTotals]     = useState(null);
  const [byGroup, setByGroup]           = useState([]);
  const [attendance, setAttendance]     = useState([]);
  const [trend, setTrend]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendPeriod, setTrendPeriod]   = useState(() => localStorage.getItem('ccs_gran') || 'daily');
  const setGran = (g) => { localStorage.setItem('ccs_gran', g); setTrendPeriod(g); };
  const trendChartRef = useRef(null);
  const svcChartRef   = useRef(null);
  const avgChartRef   = useRef(null);
  const commChartRef  = useRef(null);
  const donebaseRef   = useRef(null);
  const collectionRef = useRef(null);
  const [donebaseData, setDonebaseData] = useState({ total:0, by_staff:[], head_G:0, avg_head:0 });
  const [selCommBar,  setSelCommBar]  = useState(null);
  const [selDaysBar,  setSelDaysBar]  = useState(null);

  // Force monthly granularity for All Time to prevent massive daily datasets
  useEffect(() => {
    if (activePeriod === 'all' && trendPeriod !== 'monthly') {
      setGran('monthly');
    }
  }, [activePeriod]);
  const [collapsed, setCollapsed]       = useState({});

  // Advanced filters - pending until Apply is clicked
  const [filtersOpen, setFO]            = useState(false);
  const [filterOpts,  setFilterOpts]    = useState({ staff:[], classify:[], classify_a:[], classify_b:[], products:[], payment_modes:[], branches:[] });
  const _initFilters = () => { try { return JSON.parse(localStorage.getItem('ccs_filters') || '{}'); } catch { return {}; } };
  const _sf = _initFilters();
  const [activeFilters, setAF]   = useState({ staff: _sf.staff || [], classify: _sf.classify || [], classify_a: _sf.classify_a || [], classify_b: _sf.classify_b || [], product: _sf.product || [], payment_mode: _sf.payment_mode || [] });
  const [pendingFilters, setPF_] = useState({ staff: _sf.staff || [], classify: _sf.classify || [], classify_a: _sf.classify_a || [], classify_b: _sf.classify_b || [], product: _sf.product || [], payment_mode: _sf.payment_mode || [] });
  const applyFilters = (f) => { localStorage.setItem('ccs_filters', JSON.stringify(f)); setAF(f); };

useEffect(() => {
  getVisualPrefs().then(r => setCollapsed(r.data || {})).catch(() => {});
}, []);

useEffect(() => {
  const p = {};
  if (dateFrom) p.date_from = dateFrom;
  if (dateTo)   p.date_to   = dateTo;
  if (selectedBranches.length) p.branch_code = selectedBranches.join(',');
  getSalesFilterOptions(p).then(r => setFilterOpts(r.data || {})).catch(() => {});
}, [dateFrom, dateTo, selectedBranches]);

  useEffect(() => {
    if (activePeriod === 'custom') return;
    const now = new Date();
    const _p = n => String(n).padStart(2, '0');
    const _f = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    let from = '', to = '';
    if (activePeriod === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      from = to = _f(y);
    } else if (activePeriod === 'this_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth(), 1));
      to   = _f(now);
    } else if (activePeriod === 'this_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'this_year') {
      from = _f(new Date(now.getFullYear(), 0, 1));
      to   = _f(now);
    } else if (activePeriod === 'last_year') {
      from = _f(new Date(now.getFullYear() - 1, 0, 1));
      to   = _f(new Date(now.getFullYear() - 1, 11, 31));
    } else if (activePeriod === 'last_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      to   = _f(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (activePeriod === 'last_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day - 6); // last Monday
      const e = new Date(d); e.setDate(d.getDate() + 6); // last Sunday
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'last_year_month') {
      const m = now.getMonth();
      const ly = now.getFullYear() - 1;
      from = _f(new Date(ly, m, 1));
      to   = _f(new Date(ly, m + 1, 0));
    }
    setDateFrom(from);
    setDateTo(to);
  }, [activePeriod]);

  useEffect(() => { loadData(); }, [dateFrom, dateTo, activeFilters, selectedBranches]);
  useEffect(() => { loadTrend(); }, [trendPeriod, dateFrom, dateTo, activeFilters]);

  const dateParams = useCallback(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length > 0) p.branch_code = selectedBranches.join(',');
    return p;
  }, [dateFrom, dateTo, selectedBranches]);

  const allParams = useCallback(() => {
    const p = { ...dateParams() };
    if (activeFilters.staff.length)        p.staff        = activeFilters.staff.join(',');
    if (activeFilters.classify.length)     p.classify     = activeFilters.classify.join(',');
    if (activeFilters.classify_a.length)   p.classify_a   = activeFilters.classify_a.join(',');
    if (activeFilters.classify_b.length)   p.classify_b   = activeFilters.classify_b.join(',');
    if (activeFilters.product.length)      p.product      = activeFilters.product.join(',');
    if (activeFilters.payment_mode.length) p.payment_mode = activeFilters.payment_mode.join(',');
    return p;
  }, [dateParams, activeFilters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [perfRes, attRes] = await Promise.all([
        getStaffPerformance(allParams()),
        getStaffAttendance(allParams()),
      ]);
      setPerformance(perfRes.data.data || []);
      setPerfTotals(perfRes.data.totals || null);
      setByGroup(perfRes.data.by_group || []);
      setDonebaseData({
        total:    perfRes.data.total_donebase      || 0,
        by_staff: perfRes.data.by_donebase         || [],
        head_G:   perfRes.data.total_customer_head || 0,
        avg_head: perfRes.data.avg_head_per_staff  || 0,
      });
      setAttendance(attRes.data.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadTrend = async () => {
    setTrendLoading(true);
    try {
      const p = { ...allParams(), period: trendPeriod };
      const revRes = await getStaffTrend(p);
      setTrend(revRes.data.data || []);
    } catch(e) { console.error(e); }
    finally { setTrendLoading(false); }
  };

  const applyCustom = () => {
    localStorage.setItem('ccs_from', pendingFrom);
    localStorage.setItem('ccs_to', pendingTo);
    setDateFrom(pendingFrom);
    setDateTo(pendingTo);
  };

  const toggleCollapse = useCallback((key) => {
    const next = !collapsed[key];
    setCollapsed(prev => ({ ...prev, [key]: next }));
    setVisualPref({ visual_key: key, is_collapsed: next }).catch(() => {});
  }, [collapsed]);

  // ── derived ───────────────────────────────────────────────────────────────

const totalRevenue    = perfTotals ? parseFloat(perfTotals.total_revenue    || 0) : performance.reduce((s, r) => s + parseFloat(r.revenue   || 0), 0);
const totalTxns       = perfTotals ? parseInt(perfTotals.total_services     || 0) : performance.reduce((s, r) => s + parseInt(r.transactions|| 0), 0);
const totalCommission = perfTotals ? parseFloat(perfTotals.total_commission || 0) : performance.reduce((s, r) => s + parseFloat(r.commission|| 0), 0);
const avgPerTxn       = totalTxns > 0 ? totalRevenue / totalTxns : 0;

  // Build working days/hours maps from attendance
  const workingDays  = {};
  const workingHours = {};
  attendance.forEach(a => {
    const key = a.hr_name || a.hr_code;
    if (!workingDays[key])  workingDays[key]  = 0;
    if (!workingHours[key]) workingHours[key] = 0;
    if (a.check_in) workingDays[key]++;
    if (a.hours_worked) workingHours[key] += parseFloat(a.hours_worked || 0);
  });

  const trendPeriodKeys = generateFilledPeriodKeys(
    sortPeriodKeys([...new Set(trend.map(t => t.period))]),
    trendPeriod
  );
  const trendStaff      = [...new Set(trend.map(t => t.staff_name || t.sold_code))];
  const saleGroups      = [...new Set(byGroup.map(b => b.sale_group))];

  // ── legend state (one shared set — all trend charts show same staff) ─────────
  const trendLegend = useChartLegend(trendStaff);

  // ── legend state for donebase pie and collection pie ─────────────────────────
  const donebaseItems    = donebaseData.by_staff.filter(r => parseFloat(r.donebase||0) > 0).map(r => r.staff_name||r.sold_code);
  const donebaseLegend   = useChartLegend(donebaseItems);
  const collectionItems  = performance.filter(r => parseFloat(r.revenue||0) > 0).map(r => r.staff_name||r.sold_code);
  const collectionLegend = useChartLegend(collectionItems);

  // Pre-build O(1) lookup: "isoKey||staffName" → trend row
  const trendIndex = {};
  trend.forEach(t => {
    const k = `${toISOKey(t.period)}||${t.staff_name || t.sold_code}`;
    trendIndex[k] = t;
  });

  // For the category chart we sort staff by their total revenue (desc)
  const staffTotals = {};
  byGroup.forEach(b => {
    const name = b.staff_name || b.sold_code;
    staffTotals[name] = (staffTotals[name] || 0) + parseFloat(b.revenue || 0);
  });
  const staffNames = [...new Set(byGroup.map(b => b.staff_name || b.sold_code))]
    .sort((a, b) => (staffTotals[b] || 0) - (staffTotals[a] || 0));

  const multiYear = isMultiYear(trendPeriodKeys);
  const markArea  = buildMarkArea(trendPeriodKeys);

  // ── table sort state (must be at component level — Rules of Hooks) ──────────
  const staffWithDays = performance.map(s => ({ ...s, working_days: workingDays[s.staff_name || s.sold_code] || 0 }));
  const { sorted: staffSorted, sortCol: sCol, sortDir: sDir, toggleSort: sSort } = useSortedData(staffWithDays, 'revenue');

  // ── performance sorted desc by revenue (for all bar charts) ──────────────
  const perfSortedByRevenue = [...performance].sort((a, b) =>
    parseFloat(b.revenue || 0) - parseFloat(a.revenue || 0)
  );
  const perfSortedByTxn = [...performance].sort((a, b) =>
    parseInt(b.transactions || 0) - parseInt(a.transactions || 0)
  );
  const perfSortedByComm = [...performance].sort((a, b) =>
    parseFloat(b.commission || 0) - parseFloat(a.commission || 0)
  );

  // Working days/hours sorted desc
  const wdPairs = Object.entries(workingDays).sort((a, b) => b[1] - a[1]);
  const whPairs = Object.entries(workingHours).sort((a, b) => b[1] - a[1]);

  // ── TREND CHART ───────────────────────────────────────────────────────────

  const trendChartOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: (params) => {
        const lbl = fmtDate(params[0]?.name, trendPeriod === 'monthly'
          ? { month:'short', year:'numeric' }
          : { day:'numeric', month:'short', year: multiYear ? 'numeric' : undefined });
        return lbl + '<br/>' + params
          .filter(p => (p.value || 0) > 0)
          .map(p => `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>RM ${Number(p.value||0).toLocaleString(undefined,{maximumFractionDigits:0})}</b>`)
          .join('<br/>');
      },
    },
    legend: { show: false },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: {
      type:'category', data: trendPeriodKeys,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { color:C.textMuted, fontSize:9, rotate: multiYear ? 30 : 0, formatter: makeLabelFmt(trendPeriod, multiYear), hideOverlap: true },
    },
    yAxis: {
      type:'value', splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v },
    },
    series: trendStaff.map((name, i) => ({
      name, type:'line', smooth:true,
      symbol: 'circle', symbolSize: trendPeriodKeys.length > 60 ? 2 : 3,
      showSymbol: trendPeriodKeys.length <= 5,
      data: trendPeriodKeys.map(pk => {
        const found = trendIndex[`${pk}||${name}`];
        return found ? parseFloat(found.revenue || 0) : 0;
      }),
      lineStyle: { color: STAFF_COLORS[i % STAFF_COLORS.length], width: 2 },
      itemStyle: { color: STAFF_COLORS[i % STAFF_COLORS.length] },
      markArea: i === 0 && markArea.length ? { silent:true, data: markArea } : undefined,
    }))
  };

  // ── SERVICES DELIVERED TREND (derived from trend data — same API call) ────
  const svcPeriodKeys = generateFilledPeriodKeys(
    sortPeriodKeys([...new Set(trend.map(t => t.period))]),
    trendPeriod
  );
  const svcStaff      = [...new Set(trend.map(t => t.staff_name || t.sold_code))];
  const svcMultiYear  = isMultiYear(svcPeriodKeys);
  const svcMarkArea   = buildMarkArea(svcPeriodKeys);

  const svcTrendOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: (params) => {
        const lbl = fmtDate(params[0]?.name, trendPeriod === 'monthly'
          ? { month:'short', year:'numeric' }
          : { day:'numeric', month:'short', year: svcMultiYear ? 'numeric' : undefined });
        return lbl + '<br/>' + params.map(p =>
          `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>${Math.round(p.value||0)} services</b>`
        ).join('<br/>');
      },
    },
    legend: { show: false },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: {
      type:'category', data: svcPeriodKeys,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { color:C.textMuted, fontSize:9, rotate: svcMultiYear ? 30 : 0, formatter: makeLabelFmt(trendPeriod, svcMultiYear), hideOverlap: true },
    },
    yAxis: {
      type:'value', minInterval: 1, splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: v => Math.round(v) },
    },
    series: svcStaff.map((name, i) => ({
      name, type:'line', smooth:true,
      symbol: 'circle', symbolSize: svcPeriodKeys.length > 60 ? 2 : 3,
      showSymbol: svcPeriodKeys.length <= 5,
      data: svcPeriodKeys.map(pk => {
        const found = trendIndex[`${pk}||${name}`];
        return found ? parseInt(found.transactions || 0) : 0;
      }),
      lineStyle: { color: STAFF_COLORS[i % STAFF_COLORS.length], width: 2 },
      itemStyle: { color: STAFF_COLORS[i % STAFF_COLORS.length] },
      markArea: i === 0 && svcMarkArea.length ? { silent:true, data: svcMarkArea } : undefined,
    }))
  };

  // ── AVERAGE REVENUE TREND (derived from trend data — same API call) ─────────
  const avgPeriodKeys = generateFilledPeriodKeys(
    sortPeriodKeys([...new Set(trend.map(t => t.period))]),
    trendPeriod
  );
  const avgStaff      = [...new Set(trend.map(t => t.staff_name || t.sold_code))];
  const avgMultiYear  = isMultiYear(avgPeriodKeys);
  const avgMarkArea   = buildMarkArea(avgPeriodKeys);

  const avgTrendOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: (params) => {
        const lbl = fmtDate(params[0]?.name, trendPeriod === 'monthly'
          ? { month:'short', year:'numeric' }
          : { day:'numeric', month:'short', year: avgMultiYear ? 'numeric' : undefined });
        return lbl + '<br/>' + params
          .filter(p => (p.value || 0) > 0)
          .map(p => `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>RM ${Number(p.value||0).toLocaleString(undefined,{maximumFractionDigits:0})}</b>`)
          .join('<br/>');
      },
    },
    legend: { show: false },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: {
      type:'category', data: avgPeriodKeys,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { color:C.textMuted, fontSize:9, rotate: avgMultiYear ? 30 : 0, formatter: makeLabelFmt(trendPeriod, avgMultiYear), hideOverlap: true },
    },
    yAxis: {
      type:'value', splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v },
    },
    series: avgStaff.map((name, i) => ({
      name, type:'line', smooth:true,
      symbol: 'circle', symbolSize: avgPeriodKeys.length > 60 ? 2 : 3,
      showSymbol: avgPeriodKeys.length <= 5,
      data: avgPeriodKeys.map(pk => {
        const found = trendIndex[`${pk}||${name}`];
        if (!found) return 0;
        const txns = parseInt(found.transactions || 0);
        return txns > 0 ? Math.round(parseFloat(found.revenue || 0) / txns) : 0;
      }),
      lineStyle: { color: STAFF_COLORS[i % STAFF_COLORS.length], width: 2 },
      itemStyle: { color: STAFF_COLORS[i % STAFF_COLORS.length] },
      markArea: i === 0 && avgMarkArea.length ? { silent:true, data: avgMarkArea } : undefined,
    }))
  };

    // ── CATEGORY CHART (stacked, staff sorted by total revenue desc) ──────────

  const categoryChartOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
    },
    legend: {
      data: saleGroups, bottom: 0, type: 'plain',
      textStyle: { color: C.textSec, fontSize: 9 },
      padding: [4, 4, 4, 4],
    },
    grid: { left:8, right:8, top:8, bottom:80, containLabel:true },
    xAxis: {
      type:'category', data: staffNames,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { color:C.textMuted, fontSize:9, rotate:30 },
    },
    yAxis: {
      type:'value', splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v },
    },
    series: saleGroups.map((grp, i) => ({
      name: grp, type:'bar', stack:'total',
      data: staffNames.map(name => {
        const found = byGroup.find(b => (b.staff_name || b.sold_code) === name && b.sale_group === grp);
        return found ? parseFloat(found.revenue || 0) : 0;
      }),
      itemStyle: { color: STAFF_COLORS[i % STAFF_COLORS.length] },
    })),
  };

  // ── individual bar charts — all sorted descending ────────────────────────

  const revenueChartOption = buildBarOption(
    perfSortedByRevenue.map(s => s.staff_name || s.sold_code),
    perfSortedByRevenue.map(s => parseFloat(s.revenue || 0)),
    STAFF_COLORS
  );

  const txnChartOption = buildBarOption(
    perfSortedByTxn.map(s => s.staff_name || s.sold_code),
    perfSortedByTxn.map(s => parseInt(s.transactions || 0)),
    STAFF_COLORS,
    v => v
  );

  const commissionChartOption = (() => {
    const base = buildBarOption(
      perfSortedByComm.map(s => s.staff_name || s.sold_code),
      perfSortedByComm.map(s => parseFloat(s.commission || 0)),
      STAFF_COLORS
    );
    return { ...base, series: [{ ...base.series[0], data: base.series[0].data.map((item, i) => ({
      ...item, itemStyle: { ...item.itemStyle, opacity: selCommBar === null ? 1 : (selCommBar === i ? 1 : 0.3) }
    })) }] };
  })();

  const workingDaysOption = (() => {
    const base = buildBarOption(
      wdPairs.map(([k]) => k),
      wdPairs.map(([, v]) => v),
      STAFF_COLORS,
      v => v
    );
    return { ...base, series: [{ ...base.series[0], data: base.series[0].data.map((item, i) => ({
      ...item, itemStyle: { ...item.itemStyle, opacity: selDaysBar === null ? 1 : (selDaysBar === i ? 1 : 0.3) }
    })) }] };
  })();

  const workingHoursOption = buildBarOption(
    whPairs.map(([k]) => k),
    whPairs.map(([, v]) => parseFloat(v.toFixed(1))),
    STAFF_COLORS,
    v => v
  );

  const activeFiltersCount = Object.values(activeFilters).reduce((n, arr) => n + arr.length, 0);

  // ── period toggle ─────────────────────────────────────────────────────────

  const periodToggle = (
    <div style={toggleGroup}>
      {['daily','weekly','monthly'].map(p => {
        const disabled = activePeriod === 'all' && p !== 'monthly';
        return (
          <button key={p} style={{ ...toggleBtn(trendPeriod === p), opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer' }}
            onClick={() => !disabled && setGran(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        );
      })}
    </div>
  );

  const RefreshBadge = () => trendLoading ? (
    <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'4px' }}>
      <div style={{ width:'10px', height:'10px', border:`2px solid ${C.bgElevated}`, borderTopColor:C.accent, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <span style={{ fontSize:'10px', color:C.textMuted }}>Updating…</span>
    </div>
  ) : null;

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'40px', height:'40px', border:`3px solid ${C.bgElevated}`, borderTopColor:C.blue, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
        <p style={{ color:C.textMuted, fontSize:'13px' }}>Loading staff data...</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding:'16px', paddingBottom:'32px' }}>

      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
        {QUICK.map(q => <button key={q.id} onClick={() => setPeriod(q.id)} style={filterPill(activePeriod === q.id)}>{q.label}</button>)}
      </div>
          {/* Branch multi-select — boss, area_manager, superadmin only */}
          {(() => {
            try {
              const u = JSON.parse(localStorage.getItem('user') || '{}');
              if (!['boss','area_manager','superadmin'].includes(u.role)) return null;
            } catch { return null; }
            if (branches.length === 0) return null;
            const label = selectedBranches.length === 0 ? 'All Branches'
              : selectedBranches.length === 1 ? (branches.find(b => b.code === selectedBranches[0])?.name || selectedBranches[0])
              : `${selectedBranches.length} Branches`;
            return (
              <div style={{ marginBottom:'10px', position:'relative', display:'inline-block' }}>
                <button onClick={openBranchDrop}
                  style={{ ...filterPill(selectedBranches.length > 0), display:'flex', alignItems:'center', gap:'6px', padding:'6px 14px' }}>
                  🏢 {label} <span style={{ fontSize:'10px', opacity:0.7 }}>{showBranchDrop ? '▴' : '▾'}</span>
                </button>
                {showBranchDrop && (
                  <div style={{ position:'absolute', top:'38px', left:0, zIndex:999,
                    background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'12px',
                    padding:'8px', minWidth:'180px', boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
                    {branches.map(b => (
                      <label key={b.code} style={{ display:'flex', alignItems:'center', gap:'8px',
                        padding:'7px 10px', cursor:'pointer', borderRadius:'8px', fontSize:'13px',
                        color: pendingBranches.includes(b.code) ? C.accent : C.textSec,
                        background: pendingBranches.includes(b.code) ? 'rgba(168,224,99,0.1)' : 'transparent' }}>
                        <input type="checkbox" checked={pendingBranches.includes(b.code)}
                          onChange={() => toggleBranch(b.code)} style={{ accentColor: C.accent }} />
                        {b.name || b.code}
                      </label>
                    ))}
                    <div style={{ borderTop:`1px solid ${C.border}`, marginTop:'6px', paddingTop:'6px', display:'flex', flexDirection:'column', gap:'4px' }}>
                      <button onClick={applyBranches}
                        style={{ width:'100%', background:C.accent, border:'none', borderRadius:'6px',
                          color:'#000', fontSize:'12px', fontWeight:'700', cursor:'pointer', padding:'6px 0' }}>
                        Apply
                      </button>
                      <button onClick={resetBranches}
                        style={{ width:'100%', background:'transparent', border:'none',
                          color:C.textMuted, fontSize:'12px', cursor:'pointer', padding:'4px 0' }}>
                        ↩ Reset to All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

      {activePeriod === 'custom' && (
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'flex-end' }}>
          {[['From', pendingFrom, setPF], ['To', pendingTo, setPT]].map(([label, val, setter]) => (
            <div key={label} style={{ flex:1 }}>
              <p style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>{label}</p>
              <input type="date" value={val} onChange={e => setter(e.target.value)}
                style={{ width:'100%', padding:'8px 10px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'12px', color: val ? C.textPrimary : C.textMuted, outline:'none', boxSizing:'border-box' }}
              />
            </div>
          ))}
          <button onClick={applyCustom} disabled={!pendingFrom || !pendingTo}
            style={{ padding:'8px 14px', background: pendingFrom && pendingTo ? C.accent : C.bgElevated, color: pendingFrom && pendingTo ? '#000' : C.textMuted, border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor: pendingFrom && pendingTo ? 'pointer' : 'default', flexShrink:0 }}>
            Apply
          </button>
        </div>
      )}

      {/* ── ADVANCED FILTERS ── */}
      <div style={{ marginBottom:'12px' }}>
        <button onClick={() => setFO(o => !o)} style={{ ...filterPill(activeFiltersCount > 0), display:'flex', alignItems:'center', gap:'6px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
          <span style={{ fontSize:'9px' }}>{filtersOpen ? '▲' : '▼'}</span>
        </button>
        {filtersOpen && (
          <div style={{ marginTop:'8px', padding:'12px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'12px' }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', alignItems:'flex-start' }}>
              <MultiSelect
                label="Staff"
                options={(filterOpts.staff || []).map(s => ({ key: s.code, label: s.name }))}
                selected={pendingFilters.staff}
                onChange={v => setPF_(f => ({ ...f, staff: v }))}
              />
              <MultiSelect
                label="Classify"
                options={(filterOpts.classify || [])}
                selected={pendingFilters.classify}
                onChange={v => setPF_(f => ({ ...f, classify: v }))}
              />
              <MultiSelect
                label="Classify A"
                options={(filterOpts.classify_a || [])}
                selected={pendingFilters.classify_a}
                onChange={v => setPF_(f => ({ ...f, classify_a: v }))}
              />
              <MultiSelect
                label="Classify B"
                options={(filterOpts.classify_b || [])}
                selected={pendingFilters.classify_b}
                onChange={v => setPF_(f => ({ ...f, classify_b: v }))}
              />
              <MultiSelect
                label="Product"
                options={(filterOpts.products || [])}
                selected={pendingFilters.product}
                onChange={v => setPF_(f => ({ ...f, product: v }))}
              />
              <MultiSelect
                label="Payment Mode"
                options={(filterOpts.payment_modes || [])}
                selected={pendingFilters.payment_mode}
                onChange={v => setPF_(f => ({ ...f, payment_mode: v }))}
              />
              <button
                onClick={() => { const empty = { staff:[], classify:[], classify_a:[], classify_b:[], product:[], payment_mode:[] }; setPF_(empty); applyFilters(empty); }}
                style={{ alignSelf:'flex-end', fontSize:'11px', color:C.danger, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'5px 10px', cursor:'pointer', whiteSpace:'nowrap' }}
              >
                Reset All
              </button>
            </div>
            <div style={{ marginTop:'10px', display:'flex', justifyContent:'flex-end' }}>
              <button
                onClick={() => { applyFilters({ ...pendingFilters }); setFO(false); }}
                style={{ padding:'7px 20px', background:C.accent, color:'#000', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <CollapsibleSection visKey="sp_kpi" title="Summary" collapsed={!!collapsed['sp_kpi']} onToggle={toggleCollapse}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
          <KPICard title="Total Collection" value={`RM ${(totalRevenue||0).toLocaleString(undefined,{maximumFractionDigits:0})}`}                iconBg={C.accentDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke={C.accent} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="DoneBase Sales"   value={`RM ${(parseFloat(donebaseData.total)||0).toLocaleString(undefined,{maximumFractionDigits:0})}`} iconBg={C.blueDim}   icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" stroke={C.blue} strokeWidth="2"/></svg>} />
          <KPICard title="Customer Head"    value={(donebaseData.head_G||0).toLocaleString()}               iconBg={C.amberDim}  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke={C.amber} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Avg Head per Staff"   value={(donebaseData.avg_head||0).toFixed(1)}                   iconBg={C.purpleDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" stroke={C.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>} />
        </div>
      </CollapsibleSection>

      {/* ── DONEBASE BY STAFF PIE ── */}
      {donebaseData.by_staff.length > 0 && (
        <CollapsibleSection visKey="sp_donebase" title="DoneBase Sales by Staff (Service By)" collapsed={!!collapsed['sp_donebase']} onToggle={toggleCollapse}>
          <ReactECharts ref={donebaseRef} option={(() => {
            const data = donebaseData.by_staff.filter(r => parseFloat(r.donebase||0) > 0);
            return {
              backgroundColor: 'transparent',
              tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{color:C.textPrimary,fontSize:11},
                formatter: p => `${p.name}<br/>RM ${Number(p.value).toLocaleString(undefined,{maximumFractionDigits:0})}<br/>${p.percent}%` },
              legend: { show: false, data: data.map(r => r.staff_name||r.sold_code) },
              series: [{ type:'pie', radius:['38%','65%'], center:['50%','50%'],
                data: data.map((r,i) => ({ name: r.staff_name||r.sold_code, value: parseFloat(r.donebase||0), itemStyle:{color:STAFF_COLORS[i%STAFF_COLORS.length]} })),
                label:{ show:false } }],
            };
          })()} style={{ height:'200px', width:'100%' }} />
          <ChartLegend items={donebaseItems} colors={STAFF_COLORS} chartRef={donebaseRef}
            selected={donebaseLegend.selected} onToggle={donebaseLegend.toggle}
            onSelectAll={donebaseLegend.selectAll} onReset={donebaseLegend.resetAll} />
        </CollapsibleSection>
      )}

      {/* ── COLLECTION BY STAFF (SOLD BY) — moved from Sales tab ── */}
      {performance.filter(r => parseFloat(r.revenue||0) > 0).length > 0 && (
        <CollapsibleSection visKey="sp_collection" title="Collection by Staff (Sold By)" collapsed={!!collapsed['sp_collection']} onToggle={toggleCollapse}>
          <ReactECharts ref={collectionRef} option={(() => {
            const data = performance.filter(r => parseFloat(r.revenue||0) > 0);
            return {
              backgroundColor: 'transparent',
              tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{color:C.textPrimary,fontSize:11},
                formatter: p => `${p.name}<br/>RM ${Number(p.value).toLocaleString(undefined,{maximumFractionDigits:0})}<br/>${p.percent}%` },
              legend: { show: false, data: data.map(r => r.staff_name||r.sold_code) },
              series: [{ type:'pie', radius:['38%','65%'], center:['50%','50%'],
                data: data.map((r,i) => ({ name: r.staff_name||r.sold_code, value: parseFloat(r.revenue||0), itemStyle:{color:STAFF_COLORS[i%STAFF_COLORS.length]} })),
                label:{ show:false } }],
            };
          })()} style={{ height:'200px', width:'100%' }} />
          <ChartLegend items={collectionItems} colors={STAFF_COLORS} chartRef={collectionRef}
            selected={collectionLegend.selected} onToggle={collectionLegend.toggle}
            onSelectAll={collectionLegend.selectAll} onReset={collectionLegend.resetAll} />
        </CollapsibleSection>
      )}

      {trendStaff.length > 0 && (
        <CollapsibleSection visKey="sp_trend" title="Revenue Trend per Staff" collapsed={!!collapsed['sp_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
          <RefreshBadge />
          <ReactECharts ref={trendChartRef} option={trendChartOption} style={{ height:'240px', width:'100%' }} />
          <ChartLegend
            items={trendStaff} colors={STAFF_COLORS} chartRef={trendChartRef}
            selected={trendLegend.selected}
            onToggle={trendLegend.toggle}
            onSelectAll={trendLegend.selectAll}
            onReset={trendLegend.resetAll}
          />
        </CollapsibleSection>
      )}

      {trendStaff.length > 0 && (
        <CollapsibleSection visKey="sp_svc_trend" title="Services Delivered Trend" collapsed={!!collapsed['sp_svc_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
          <RefreshBadge />
          <ReactECharts ref={svcChartRef} option={svcTrendOption} style={{ height:'240px', width:'100%' }} />
          <ChartLegend
            items={trendStaff} colors={STAFF_COLORS} chartRef={svcChartRef}
            selected={trendLegend.selected}
            onToggle={trendLegend.toggle}
            onSelectAll={trendLegend.selectAll}
            onReset={trendLegend.resetAll}
          />
        </CollapsibleSection>
      )}

      {/* Bar charts: Commission */}

      <CollapsibleSection visKey="sp_commission" title="Commission per Staff" collapsed={!!collapsed['sp_commission']} onToggle={toggleCollapse}>
        <ReactECharts option={commissionChartOption} style={{ height:'220px' }}
          onEvents={{ click: (p) => setSelCommBar(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
        {selCommBar !== null && <button onClick={() => setSelCommBar(null)} style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>Clear highlight</button>}
      </CollapsibleSection>

      {wdPairs.length > 0 && (
        <CollapsibleSection visKey="sp_days" title="Working Days per Staff" collapsed={!!collapsed['sp_days']} onToggle={toggleCollapse}>
          <ReactECharts option={workingDaysOption} style={{ height:'200px' }}
            onEvents={{ click: (p) => setSelDaysBar(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
          {selDaysBar !== null && <button onClick={() => setSelDaysBar(null)} style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>Clear highlight</button>}
        </CollapsibleSection>
      )}

      

      {/* Summary table — sortable */}
      <CollapsibleSection visKey="sp_table" title="Staff Summary" collapsed={!!collapsed['sp_table']} onToggle={toggleCollapse}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead>
              <tr>
                <SortTh col="staff_name"         label="Staff"      sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                <SortTh col="revenue"             label="Revenue"    sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                <SortTh col="transactions"        label="Services"   sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                <SortTh col="avg_per_transaction" label="Avg/Sale"   sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                <SortTh col="commission"          label="Commission" sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
                <SortTh col="working_days"        label="Days"       sortCol={sCol} sortDir={sDir} onSort={sSort} style={th} />
              </tr>
            </thead>
            <tbody>
              {staffSorted.map((s, i) => (
                <tr key={i}>
                  <td style={{ ...td, color:C.textPrimary, fontWeight:'500' }}>{s.staff_name || s.sold_code}</td>
                  <td style={tdR}>RM {parseFloat(s.revenue || 0).toLocaleString(undefined, { maximumFractionDigits:0 })}</td>
                  <td style={tdR}>{s.transactions}</td>
                  <td style={tdR}>RM {parseFloat(s.avg_per_transaction || 0).toFixed(0)}</td>
                  <td style={tdR}>RM {parseFloat(s.commission || 0).toLocaleString(undefined, { maximumFractionDigits:0 })}</td>
                  <td style={tdR}>{s.working_days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default StaffPerformance;